import React, { useId, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { LntButton, LntDrawer, LntForm, LntFormGroup, LntFormItem, LntSelect } from '@app/components/base'

import { LOCATION_KEY, MONITORING_VALIDATION_KEY } from '@app/constants/queryKeys'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { SIZE } from '@app/constants'

import { useNotification } from '@app/hooks'

import { getCompetitorsNetworks } from '@app/api/routes/locationCompetitorsNetworks'
import { getCompetitors } from '@app/api/routes/locationCompetitors'
import { postMonitoringValidationConfigStoresLinks } from '@app/api/routes/monitoringValidation'

import { getFilterCompetitorsFormEntities, getFilterFromEntities } from '@app/utils/entitiesAdapters'

import styles from './styles.module.scss'

import type { AddPriceRestorationDrawerFormData, AddPriceRestorationDrawerProps } from './types'
import type { LntSelectProps } from '@app/components/base/Select/types'
import type { PostMonitoringValidationConfigStoresLinksData } from '@app/api/routes/monitoringValidation/types'
import type { GetCompetitorsParams } from '@app/api/routes/locationCompetitors/types'

const commonSelectProps: LntSelectProps = {
  showArrow: true,
  size: SIZE.middle,
  strictValue: true
}

const title = 'Добавление связи'

export const AddPriceRestorationDrawer: React.FC<AddPriceRestorationDrawerProps> = props => {
  const { onClose } = props

  const formId = useId()
  const form = useForm<AddPriceRestorationDrawerFormData>()
  const queryClient = useQueryClient()
  const notification = useNotification()

  const [firstNetworkId, setFirstNetworkId] = useState('')
  const [secondNetworkId, setSecondNetworkId] = useState('')

  const firstNetworkIdParams = useMemo<GetCompetitorsParams>(() => ({ networkIds: [firstNetworkId] }), [firstNetworkId])

  const secondNetworkIdParams = useMemo(() => {
    return { networkIds: [secondNetworkId] }
  }, [secondNetworkId])

  const postTranslationsConfigCompetitorsMutation = useMutation({
    mutationFn: postMonitoringValidationConfigStoresLinks,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.storesLinks] })

      notification.success({
        message: 'Связь успешно добавлена.'
      })

      onClose?.()
    }
  })

  const handleSubmit = (data: PostMonitoringValidationConfigStoresLinksData): void => {
    postTranslationsConfigCompetitorsMutation.mutate(data)
  }

  const footerButtons: React.ReactNode = (
    <div className={ styles.addPriceRestorationDrawer__footer }>
      <LntButton
        htmlType='submit'
        form={ formId }
        disabled={ !form.formState.isValid }
      >
        Сохранить
      </LntButton>
    </div>
  )

  return (
    <LntDrawer
      { ...props }
      title={ title }
      width={ 424 }
      footer={ footerButtons }
    >
      <LntForm<AddPriceRestorationDrawerFormData>
        layout='vertical'
        formInstance={ form }
        id={ formId }
        onSubmit={ handleSubmit }
      >
        <div className={ styles.addPriceRestorationDrawerForm__itemsWrapper }>
          <LntFormGroup label='Выберите магазин 1'>
            <LntSelect
              { ...commonSelectProps }
              className={ styles.addPriceRestorationDrawerForm__select }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationCompetitorsItems],
                queryFn: getCompetitorsNetworks,
                select: getFilterFromEntities
              } }
              onChange={ value => setFirstNetworkId(value as string) }
              placeholder={ FILTERS_PLACEHOLDERS.competitorsNetworksFilter }
            />
            <LntFormItem
              name='firstCompetitorId'
              defaultValue={ [] }
              validationOptions={ { rules: { required: true } } }
            >
              <LntSelect
                { ...commonSelectProps }
                className={ styles.addPriceRestorationDrawerForm__select }
                disabled={ !firstNetworkId?.length }
                querySelect={ {
                  queryKey: [LOCATION_KEY.locationCompetitorsNetworksItems, firstNetworkIdParams],
                  queryFn: async () => await getCompetitors(firstNetworkIdParams),
                  select: getFilterCompetitorsFormEntities
                } }
                placeholder={ FILTERS_PLACEHOLDERS.competitorsAddressesFilter }
              />
            </LntFormItem>
          </LntFormGroup>

          <LntFormGroup label='Выберите магазин 2'>
            <LntSelect
              { ...commonSelectProps }
              className={ styles.addPriceRestorationDrawerForm__select }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationCompetitorsItems],
                queryFn: getCompetitorsNetworks,
                select: getFilterFromEntities
              } }
              onChange={ value => setSecondNetworkId(value as string) }
              placeholder={ FILTERS_PLACEHOLDERS.competitorsNetworksFilter }
            />
            <LntFormItem
              name='secondCompetitorId'
              defaultValue={ [] }
              validationOptions={ { rules: { required: true } } }
            >
              <LntSelect
                { ...commonSelectProps }
                className={ styles.addPriceRestorationDrawerForm__select }
                disabled={ !secondNetworkId?.length }
                querySelect={ {
                  queryKey: [LOCATION_KEY.locationCompetitorsNetworksItems, secondNetworkIdParams],
                  queryFn: async () => await getCompetitors(secondNetworkIdParams),
                  select: getFilterCompetitorsFormEntities
                } }
                placeholder={ FILTERS_PLACEHOLDERS.competitorsAddressesFilter }
              />
            </LntFormItem>
          </LntFormGroup>
        </div>
      </LntForm>
    </LntDrawer>
  )
}
